// =============================================================
// LibraryPanel.jsx — the company library on Gov Opportunities.
//
// Per bidding company: past contracts (each with its RFP, contract, proposal,
// award …) and company material (capability statements, résumés, boilerplate).
// "Read files with AI" fills a SUGGESTION beside the form; nothing on the record
// changes until the person applies it and saves. Only a CONFIRMED past contract
// reaches the bid screen.
//
// Every uploaded file is also read on its own (the per-file scan, migration
// 240): what it is, its key facts and a summary, saved per file. The upload
// fires the scan; the server's sweep finishes anything left; this screen polls
// while files are being read.
// =============================================================
import { useState, useEffect, useCallback, useMemo } from 'react';
import { B } from '../../constants/brand';
import { fmt } from '../../utils/format';
import { api } from '../../api/client';
import FileDropZone from '../shared/FileDropZone';
import { collectEntriesFromInputFiles, collectEntriesFromDataTransfer } from '../../utils/docFolders';
import { planLibraryImport, uploadQueue, runPool, looksLikeSubfolders } from '../../utils/libraryImport';
import { searchWords, fileTextIndex, recordMatches, fileTextReasons } from '../../utils/librarySearch';
import { Btn, Input, Textarea, Select, Card, Badge, Spinner, EmptyState, Modal, ModalFooter, useToast } from '../ui';

const ROLE_LABELS = { prime: 'Prime', sub: 'Subcontractor', jv: 'Joint venture' };
const OUTCOME_LABELS = { won: 'Won', lost: 'Lost', pending: 'Pending' };
const MATERIAL_LABELS = { capability: 'Capability statement', resume: 'Résumé', boilerplate: 'Boilerplate', other: 'Other' };
const FILE_ROLE_LABELS = {
  rfp: 'RFP / solicitation', contract: 'Contract / task order', proposal: 'Our proposal', award: 'Award notice',
  evaluation: 'Evaluation / CPARS', modification: 'Modification', material: 'Company material', other: 'Other',
};
const FIELD_LABELS = {
  title: 'Title', agency: 'Agency / customer', contractRef: 'Contract number', contractRole: 'Our role',
  primeContractor: 'Prime contractor', outcome: 'Outcome', contractAmount: 'Contract value',
  periodStartDate: 'Start', periodEndDate: 'End', naicsCode: 'NAICS', referenceContact: 'Reference',
  scope: 'Scope of work', results: 'How it went',
};
const SUGGESTED_FIELDS = Object.keys(FIELD_LABELS);
const outcomeColor = (o) => (o === 'won' ? B.green : o === 'lost' ? B.red : B.textMd);
const shortMoney = (n) => (n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `$${Math.round(n / 1e3)}k` : fmt.currency(n));

// Every string and number inside an AI scan or suggestion, flattened for search.
function textOf(v, depth = 0) {
  if (v === null || v === undefined || depth > 4) return '';
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (Array.isArray(v)) return v.map(x => textOf(x, depth + 1)).join(' ');
  if (typeof v === 'object') return Object.values(v).map(x => textOf(x, depth + 1)).join(' ');
  return '';
}

export function LibraryPanel() {
  const { toast } = useToast();
  const [companies, setCompanies] = useState(null);
  // '' = every company. The list always loads whole; the filter is applied here.
  const [entityId, setEntityId] = useState('');
  // Free text over every field on screen plus the file names; every word must match.
  const [query, setQuery] = useState('');
  const [lib, setLib] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [importing, setImporting] = useState(false);
  const [reviewing, setReviewing] = useState(null); // item ids queued for review
  // Full-text hits from the server (every word of every file, migration 245).
  const [textHits, setTextHits] = useState(null); // { q, result } | null
  const [textSearching, setTextSearching] = useState(false);
  useEffect(() => {
    const q = query.trim();
    if (!q) { setTextHits(null); setTextSearching(false); return undefined; }
    let live = true;
    setTextSearching(true);
    const t = setTimeout(() => {
      api.get(`govbid/library-search?q=${encodeURIComponent(q)}`)
        .then(result => { if (live) setTextHits({ q, result }); })
        .catch(() => { if (live) setTextHits({ q, result: { available: false, failed: true } }); })
        .finally(() => { if (live) setTextSearching(false); });
    }, 350);
    return () => { live = false; clearTimeout(t); };
  }, [query]);

  useEffect(() => {
    api.get('govbid/profiles').then(d => {
      const list = (d?.profiles || []).slice().sort((a, b) => (b.isBidding - a.isBidding) || String(a.entityCode).localeCompare(b.entityCode));
      setCompanies(list);
    }).catch(e => setError(e.message));
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setLib(await api.get('govbid/library')); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const [adding, setAdding] = useState(null); // kind waiting for a company to be picked
  const addRecord = async (kind, forEntity = entityId) => {
    if (!forEntity) { setAdding(kind); return; }
    try {
      const item = await api.post('govbid/library', { entityId: forEntity, kind, ...(kind === 'material' ? { materialType: 'capability' } : {}) });
      setOpen(item);
    } catch (e) { toast.error(e.message); }
  };

  const onChanged = (item, { removed } = {}) => {
    setLib(l => {
      if (!l) return l;
      const idx = l.items.findIndex(i => i.itemId === item.itemId);
      if (removed) return { ...l, items: l.items.filter(i => i.itemId !== item.itemId) };
      if (idx < 0) return { ...l, items: [item, ...l.items] };
      const next = l.items.slice(); next[idx] = item;
      return { ...l, items: next };
    });
  };

  const nameOf = useMemo(() => {
    const m = new Map((companies || []).map(c => [String(c.sageEntityId).toLowerCase(), c.entityName || c.entityCode]));
    return (id) => m.get(String(id || '').toLowerCase()) || 'Unknown company';
  }, [companies]);

  if (companies === null) return error ? <Card><div style={{ color: B.red, fontSize: 13 }}>{error}</div></Card>
    : <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>;
  if (!companies.length) return <EmptyState title="No companies yet" message="Import the Entities list on Settings → Sage Import first." />;
  if (lib && lib.available === false) return <Card><div style={{ fontSize: 13, color: B.textMd }}>{lib.reason}</div></Card>;

  const allItems = lib?.items || [];
  const sameId = (a, b) => String(a || '').toLowerCase() === String(b || '').toLowerCase();
  const byCompany = entityId ? allItems.filter(i => sameId(i.sageEntityId, entityId)) : allItems;
  const terms = searchWords(query);
  const textIndex = textHits && textHits.q === query.trim() ? fileTextIndex(textHits.result) : null;
  const haystack = (i) => [
    i.title, i.agency, i.contractRef, i.scope, i.results, i.naicsCode, i.outcome,
    MATERIAL_LABELS[i.materialType] || i.materialType, i.body, nameOf(i.sageEntityId),
    ...(i.files || []).map(f => f.fileName),
    // What the AI read inside each file (its saved scan) and its suggested facts.
    ...(i.files || []).map(f => textOf(f.scan)),
    textOf(i.suggestion),
  ].filter(Boolean).join(' ').toLowerCase();
  const reasons = new Map(); // itemId → words found only inside a file
  const items = terms.length ? byCompany.filter(i => {
    const h = haystack(i);
    if (!recordMatches(h, i.itemId, terms, textIndex)) return false;
    const r = fileTextReasons(h, i.itemId, terms, textIndex);
    if (r.length) reasons.set(i.itemId, r);
    return true;
  }) : byCompany;
  const textInfo = textHits?.result;
  const searching = terms.length > 0;
  const countFor = (id) => allItems.filter(i => sameId(i.sageEntityId, id)).length;
  const companyOptions = [
    { value: '', label: `All companies (${allItems.length})` },
    ...companies
      .map(c => ({ c, n: countFor(c.sageEntityId) }))
      .filter(({ c, n }) => n > 0 || c.isBidding || sameId(c.sageEntityId, entityId))
      .map(({ c, n }) => ({ value: c.sageEntityId, label: `${c.entityName || c.entityCode}${c.isBidding ? '' : ' (not bidding)'} (${n})` })),
  ];
  const companyPicks = companies.map(c => ({ value: c.sageEntityId, label: `${c.entityName || c.entityCode}${c.isBidding ? '' : ' (not bidding)'}` }));
  const contracts = items.filter(i => i.kind === 'pastContract');
  const material = items.filter(i => i.kind === 'material');
  const confirmed = contracts.filter(i => i.status === 'confirmed').length;
  const toReview = contracts.filter(hasSuggestions);

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
        <div style={{ width: 340 }}>
          <Select label="Show company" value={entityId} onChange={setEntityId} options={companyOptions} />
        </div>
        <div style={{ width: 320 }}>
          <Input label="Search" value={query} onChange={setQuery}
            placeholder="Title, agency, company, file name, or any word in a file…" />
        </div>
        <div style={{ flex: 1, minWidth: 260, fontSize: 12, color: B.textMd, lineHeight: 1.5 }}>
          Add each contract a company bid on with its files. AI reads the files and suggests the facts; you check them and confirm.
          Only confirmed contracts are used to grade opportunities and, later, to draft proposals.
        </div>
      </div>
      {searching && (
        <div style={{ fontSize: 12, color: B.textMd, margin: '-4px 0 10px' }}>
          {textSearching ? 'Searching inside the files…'
            : textInfo?.failed ? 'Could not search inside the files just now — showing matches on the records only.'
            : textInfo && textInfo.available === false ? 'Searching the records only — reading the full text of files needs a database update that has not been applied yet.'
            : textInfo ? `Also searched the full text of ${textInfo.filesRead} file${textInfo.filesRead === 1 ? '' : 's'}${textInfo.filesWaiting ? ` (${textInfo.filesWaiting} still being read — they match by name and summary until then)` : ''}.${textInfo.terms?.some(t => t.truncated) ? ' A common word matched many files; only the first few hundred are counted.' : ''}`
            : null}
        </div>
      )}
      {lib && !lib.storageReady && <Card style={{ marginBottom: 12 }}><div style={{ color: B.red, fontSize: 13 }}>Blob storage is not configured, so files cannot be uploaded.</div></Card>}
      {error && <Card style={{ marginBottom: 12 }}><div style={{ color: B.red, fontSize: 13 }}>{error}</div></Card>}

      {loading && !lib ? <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div> : (
        <>
          <SectionHeader title={`Past contracts (${contracts.length}${contracts.length ? `, ${confirmed} confirmed` : ''})`}
            action={(
              <div style={{ display: 'flex', gap: 8 }}>
                {toReview.length > 0 && (
                  <Btn size="sm" variant="ghost" onClick={() => setReviewing(toReview.map(i => i.itemId))}>Review suggestions ({toReview.length})</Btn>
                )}
                <Btn size="sm" variant="ghost" onClick={() => setImporting(true)} disabled={lib && !lib.storageReady}>Import folders</Btn>
                <Btn size="sm" onClick={() => addRecord('pastContract')}>+ Add past contract</Btn>
              </div>
            )} />
          {contracts.length > 0 && <ImportProgress contracts={contracts} onRefresh={load} onReview={() => setReviewing(toReview.map(i => i.itemId))} />}
          {!contracts.length ? (
            <div style={{ fontSize: 13, color: B.textLt, margin: '4px 0 20px' }}>{searching ? 'No past contracts match the search.' : 'None yet. Add one per contract — won or lost.'}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
              {contracts.map(i => <ContractRow key={i.itemId} item={i} company={nameOf(i.sageEntityId)} reasons={reasons.get(i.itemId)} onOpen={() => setOpen(i)} />)}
            </div>
          )}
          <SectionHeader title={`Company material (${material.length})`}
            action={<Btn size="sm" variant="ghost" onClick={() => addRecord('material')}>+ Add material</Btn>} />
          {!material.length ? (
            <div style={{ fontSize: 13, color: B.textLt, marginTop: 4 }}>{searching ? 'No company material matches the search.' : 'Capability statements, résumés, reusable proposal text.'}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {material.map(i => (
                <Card key={i.itemId} interactive style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: 'pointer' }} onClick={() => setOpen(i)}>
                  <Badge color={B.navy}>{MATERIAL_LABELS[i.materialType] || 'Material'}</Badge>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: B.text }}>{i.title || 'Untitled'}</div>
                    <div style={{ fontSize: 12, color: B.textMd, marginTop: 2 }}>{nameOf(i.sageEntityId)}</div>
                    <FoundIn reasons={reasons.get(i.itemId)} />
                  </div>
                  <div style={{ fontSize: 12, color: B.textLt }}>{i.files.length} file{i.files.length === 1 ? '' : 's'}</div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {importing && (
        <ImportModal initialEntityId={entityId} items={allItems} companies={companyPicks}
          onDone={() => { load(); }} onClose={() => { setImporting(false); load(); }} />
      )}

      {reviewing && (
        <ReviewModal ids={reviewing} items={allItems} nameOf={nameOf}
          onChanged={onChanged}
          onOpen={(it) => { setReviewing(null); setOpen(it); }}
          onClose={() => setReviewing(null)} />
      )}

      {adding && (
        <CompanyPickModal companies={companyPicks}
          title={adding === 'material' ? 'Add company material' : 'Add a past contract'}
          onPick={(id) => { const kind = adding; setAdding(null); addRecord(kind, id); }}
          onClose={() => setAdding(null)} />
      )}

      {open && (
        <ItemModal item={open} options={lib?.options} aiReady={lib?.aiReady} companies={companyPicks}
          onChanged={(it, o) => { onChanged(it, o); if (o?.removed) setOpen(null); else setOpen(it); }}
          onClose={() => setOpen(null)} />
      )}
    </div>
  );
}

const SCAN_LABELS = { pending: 'Waiting to be read', running: 'AI is reading…', ready: 'Read', unsupported: "Couldn't read", error: 'Read failed' };

// One file: its type (marked "AI" when the scan chose it), the scan's state,
// and the saved summary behind a toggle.
export function FileRow({ file, options, aiReady, onDownload, onRole, onRescan, onRemove }) {
  const [open, setOpen] = useState(false);
  const s = file.scan;
  const busy = file.scanStatus === 'pending' || file.scanStatus === 'running';
  const facts = s ? [
    s.agency, s.contractRef, s.contractAmount ? fmt.currency(s.contractAmount) : null,
    [s.periodStartDate, s.periodEndDate].filter(Boolean).join(' → ') || null,
    s.documentDate ? `dated ${s.documentDate}` : null, s.naicsCode ? `NAICS ${s.naicsCode}` : null,
  ].filter(Boolean) : [];
  const roleOptions = (options?.fileRoles || Object.keys(FILE_ROLE_LABELS))
    .map(r => ({ value: r, label: `${FILE_ROLE_LABELS[r] || r}${file.fileRoleSource === 'ai' && r === file.fileRole ? ' (AI)' : ''}` }));
  return (
    <div style={{ borderBottom: `1px solid ${B.border}`, paddingBottom: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
        <a href="#download" onClick={e => { e.preventDefault(); onDownload(); }} style={{ flex: 1, color: B.text, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {file.fileName}
        </a>
        <span style={{ fontSize: 12, color: B.textLt }}>{file.sizeBytes ? `${Math.max(1, Math.round(file.sizeBytes / 1024))} KB` : ''}</span>
        {file.scanStatus && (
          <span title={file.scanError || undefined} style={{ fontSize: 12, whiteSpace: 'nowrap',
            color: file.scanStatus === 'ready' ? B.green : busy ? B.textMd : B.amber }}>
            {SCAN_LABELS[file.scanStatus] || file.scanStatus}
          </span>
        )}
        <div style={{ width: 200 }}>
          <Select value={file.fileRole} onChange={onRole} options={roleOptions} />
        </div>
        {s && <Btn size="sm" variant="ghost" onClick={() => setOpen(o => !o)}>{open ? 'Hide' : 'Summary'}</Btn>}
        {aiReady && file.scanStatus && !busy && (
          <Btn size="sm" variant="ghost" onClick={onRescan}>{file.scanStatus === 'ready' ? 'Re-read' : 'Read'}</Btn>
        )}
        <Btn size="sm" variant="ghost" onClick={onRemove}>Remove</Btn>
      </div>
      {(file.scanStatus === 'unsupported' || file.scanStatus === 'error') && file.scanError && (
        <div style={{ fontSize: 12, color: B.amber, marginTop: 2 }}>{file.scanError}</div>
      )}
      {open && s && (
        <div style={{ fontSize: 13, color: B.text, background: B.surfaceMuted || B.bg, borderRadius: 8, padding: '8px 10px', marginTop: 6 }}>
          <div style={{ fontWeight: 600 }}>{s.documentType || FILE_ROLE_LABELS[s.fileRole] || s.fileRole}{s.title ? ` — ${s.title}` : ''}</div>
          {facts.length > 0 && <div style={{ fontSize: 12, color: B.textMd, marginTop: 2 }}>{facts.join(' · ')}</div>}
          {s.parties?.length > 0 && <div style={{ fontSize: 12, color: B.textMd, marginTop: 2 }}>Parties: {s.parties.join('; ')}</div>}
          {s.summary && <div style={{ marginTop: 6, whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>{s.summary}</div>}
          {s.keywords?.length > 0 && <div style={{ fontSize: 12, color: B.textLt, marginTop: 6 }}>{s.keywords.join(' · ')}</div>}
          <div style={{ fontSize: 11, color: B.textLt, marginTop: 6 }}>Read by AI{file.scannedUTC ? ` ${fmt.date(file.scannedUTC)}` : ''} — check figures against the file.</div>
        </div>
      )}
    </div>
  );
}

function SectionHeader({ title, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '8px 0 8px' }}>
      <div style={{ fontWeight: 600, color: B.navy, fontSize: 14 }}>{title}</div>
      {action}
    </div>
  );
}

// Asked before Add when the list is showing every company: a record always belongs to one.
function CompanyPickModal({ companies, title, onPick, onClose }) {
  const [id, setId] = useState('');
  return (
    <Modal title={title} onClose={onClose} width={460}>
      <div style={{ padding: '4px 24px 8px' }}>
        <Select label="Company" value={id} onChange={setId}
          options={[{ value: '', label: 'Choose a company…' }, ...companies]} />
      </div>
      <ModalFooter>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={() => onPick(id)} disabled={!id}>Continue</Btn>
      </ModalFooter>
    </Modal>
  );
}

// Why a record matched when a word was found only inside one of its files.
function FoundIn({ reasons }) {
  if (!reasons || !reasons.length) return null;
  return (
    <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {reasons.slice(0, 2).map(r => (
        <div key={r.word} style={{ fontSize: 12, color: B.textMd, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <span style={{ color: B.navy, fontWeight: 600 }}>“{r.word}” in {r.fileName}:</span> {r.snippet}
        </div>
      ))}
    </div>
  );
}

function ContractRow({ item: i, company, reasons, onOpen }) {
  const period = [i.periodStartDate?.slice(0, 4), i.periodEndDate?.slice(0, 4)].filter(Boolean).join('–');
  return (
    <Card interactive style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: 'pointer' }} onClick={onOpen}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: B.text }}>{i.title || 'Untitled contract'}</div>
        <div style={{ fontSize: 12, color: B.textMd, marginTop: 2 }}>
          <span style={{ color: B.navy, fontWeight: 600 }}>{company}</span>
          {' · '}
          {[i.agency, i.contractRef, i.contractRole && ROLE_LABELS[i.contractRole], i.contractAmount ? shortMoney(i.contractAmount) : null, period || null,
            `${i.files.length} file${i.files.length === 1 ? '' : 's'}`].filter(Boolean).join(' · ')}
        </div>
        <FoundIn reasons={reasons} />
      </div>
      <ReadState item={i} />
      {i.outcome && <Badge color={outcomeColor(i.outcome)}>{OUTCOME_LABELS[i.outcome]}</Badge>}
      {i.status === 'confirmed' ? <Badge color={B.green}>Confirmed</Badge> : <Badge color={B.amber}>Draft</Badge>}
    </Card>
  );
}

const formFrom = (i) => ({
  title: i.title || '', agency: i.agency || '', contractRef: i.contractRef || '', contractRole: i.contractRole || '',
  primeContractor: i.primeContractor || '', outcome: i.outcome || '',
  contractAmount: i.contractAmount === null || i.contractAmount === undefined ? '' : String(i.contractAmount),
  periodStartDate: i.periodStartDate || '', periodEndDate: i.periodEndDate || '', naicsCode: i.naicsCode || '',
  scope: i.scope || '', results: i.results || '', referenceContact: i.referenceContact || '',
  materialType: i.materialType || 'other', body: i.body || '',
});

function ItemModal({ item, options, aiReady, companies, onChanged, onClose }) {
  const { toast } = useToast();
  const isContract = item.kind === 'pastContract';
  const [f, setF] = useState(() => formFrom(item));
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [uploadRole, setUploadRole] = useState(isContract ? 'auto' : 'material');
  const set = (k, v) => { setF(s => ({ ...s, [k]: v })); setErr(null); };

  useEffect(() => { setF(formFrom(item)); }, [item.itemId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll while any file is waiting to be read; bounded at ~10 minutes.
  const scanning = item.files.some(x => x.scanStatus === 'pending' || x.scanStatus === 'running');
  useEffect(() => {
    if (!scanning) return undefined;
    let n = 0;
    const id = setInterval(async () => {
      n += 1;
      if (n > 120) { clearInterval(id); return; }
      try { onChanged(await api.get(`govbid/library/${item.itemId}`)); } catch { /* next tick */ }
    }, 5000);
    return () => clearInterval(id);
  }, [scanning, item.itemId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Read the new files one after another, in the background. The response is
  // not needed: the poll above shows each result, and the server's sweep reads
  // anything this does not reach (a closed tab, a timed-out request).
  const scanInBackground = async (fileIds) => {
    for (const fid of fileIds) {
      try { await api.post(`govbid/libfiles/${fid}/scan`, {}, { timeout: 150000 }); } catch { /* the sweep retries */ }
    }
  };

  const rescan = async (file) => {
    onChanged({ ...item, files: item.files.map(x => (x.fileId === file.fileId ? { ...x, scanStatus: 'running', scanError: null } : x)) });
    try {
      const out = await api.post(`govbid/libfiles/${file.fileId}/scan`, { force: true }, { timeout: 150000 });
      if (!out.ran && out.reason) toast.error(out.reason);
      onChanged(await api.get(`govbid/library/${item.itemId}`));
    } catch (e) { toast.error(e.message); }
  };

  const dirty = useMemo(() => JSON.stringify(f) !== JSON.stringify(formFrom(item)), [f, item]);
  const suggestion = item.suggestion;
  const suggested = suggestion ? SUGGESTED_FIELDS.filter(k => suggestion[k] !== undefined && String(suggestion[k]) !== String(f[k] ?? '')) : [];

  const payload = () => (isContract
    ? { title: f.title, agency: f.agency, contractRef: f.contractRef, contractRole: f.contractRole || null,
      primeContractor: f.primeContractor, outcome: f.outcome || null, contractAmount: f.contractAmount,
      periodStartDate: f.periodStartDate, periodEndDate: f.periodEndDate, naicsCode: f.naicsCode,
      scope: f.scope, results: f.results, referenceContact: f.referenceContact }
    : { title: f.title, materialType: f.materialType, body: f.body });

  const save = async (status) => {
    setBusy(status === 'confirmed' ? 'confirm' : 'save'); setErr(null);
    try {
      const updated = await api.put(`govbid/library/${item.itemId}`, { ...payload(), ...(status ? { status } : {}) });
      onChanged(updated);
      toast.success(status === 'confirmed' ? 'Confirmed. Open opportunities will be re-graded with it.' : 'Saved.');
    } catch (e) { setErr(e.message); }
    finally { setBusy(null); }
  };

  // Saved on its own, at once: it says whose past performance this is, and
  // changes no fact on the record, so a confirmed record stays confirmed.
  const moveTo = async (id) => {
    if (!id || String(id).toLowerCase() === String(item.sageEntityId).toLowerCase()) return;
    setBusy('company');
    try {
      const updated = await api.put(`govbid/library/${item.itemId}`, { entityId: id });
      onChanged(updated);
      toast.success('Company changed.');
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${item.title || 'this record'}" and its files from the library?`)) return;
    try { await api.del(`govbid/library/${item.itemId}`); onChanged(item, { removed: true }); }
    catch (e) { toast.error(e.message); }
  };

  const close = async () => {
    // An Add that was never filled in leaves nothing behind.
    if (!item.title && !item.files.length && !dirty) {
      try { await api.del(`govbid/library/${item.itemId}`); onChanged(item, { removed: true }); return; } catch { /* keep it */ }
    }
    onClose();
  };

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy('upload');
    let next = item;
    const uploaded = [];
    try {
      for (const file of files) {
        const { fileId, uploadUrl } = await api.post(`govbid/library/${item.itemId}/files`, {
          fileName: file.name, contentType: file.type || 'application/octet-stream', sizeBytes: file.size, role: uploadRole,
        });
        const res = await fetch(uploadUrl, {
          method: 'PUT', body: file,
          headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type || 'application/octet-stream' },
        });
        if (!res.ok) throw new Error(`Upload of ${file.name} failed (${res.status}).`);
        const confirmed = await api.post(`govbid/libfiles/${fileId}/confirm`, {});
        next = { ...next, files: [...next.files, confirmed] };
        uploaded.push(confirmed);
      }
      onChanged(next);
      toast.success(`${files.length} file${files.length === 1 ? '' : 's'} uploaded.${aiReady ? ' AI is reading each one.' : ''}`);
    } catch (e) { onChanged(next); toast.error(e.message); }
    finally {
      setBusy(null);
      const queued = uploaded.filter(x => x.scanStatus === 'pending').map(x => x.fileId);
      if (queued.length) scanInBackground(queued);
    }
  };

  const setFileRole = async (file, role) => {
    try {
      await api.put(`govbid/libfiles/${file.fileId}`, { role });
      onChanged({ ...item, files: item.files.map(x => (x.fileId === file.fileId ? { ...x, fileRole: role, fileRoleSource: 'user' } : x)) });
    } catch (e) { toast.error(e.message); }
  };

  const removeFile = async (file) => {
    if (!window.confirm(`Remove ${file.fileName}?`)) return;
    try {
      await api.del(`govbid/libfiles/${file.fileId}`);
      onChanged({ ...item, files: item.files.filter(x => x.fileId !== file.fileId) });
    } catch (e) { toast.error(e.message); }
  };

  const download = async (file) => {
    const win = window.open('', '_blank');
    try {
      const { url } = await api.get(`govbid/libfiles/${file.fileId}/download-url`);
      if (win) win.location.href = url; else window.location.href = url;
    } catch (e) { if (win) win.close(); toast.error(e.message); }
  };

  const extract = async () => {
    setBusy('extract');
    try {
      const out = await api.post(`govbid/library/${item.itemId}/extract`, {}, { timeout: 180000 });
      onChanged(out.item);
      if (!out.ok) toast.error(out.error || 'The files could not be read.');
      else toast.success('Suggestions ready — check each one before you use it.');
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  const apply = (keys) => setF(s => {
    const n = { ...s };
    for (const k of keys) n[k] = suggestion[k] === null || suggestion[k] === undefined ? '' : String(suggestion[k]);
    return n;
  });

  const showSuggestion = (k, v) => (k === 'contractAmount' ? fmt.currency(Number(v))
    : k === 'contractRole' ? ROLE_LABELS[v] || v : k === 'outcome' ? OUTCOME_LABELS[v] || v : String(v));

  return (
    <Modal title={isContract ? (item.title || 'Past contract') : (item.title || 'Company material')} onClose={close} width={880}>
      <div style={{ padding: '4px 24px 8px' }}>
        <div style={{ width: 360, marginBottom: 10 }}>
          <Select label="Company" value={item.sageEntityId} onChange={moveTo} disabled={!!busy}
            options={companies.some(c => String(c.value).toLowerCase() === String(item.sageEntityId).toLowerCase())
              ? companies : [{ value: item.sageEntityId, label: 'Unknown company' }, ...companies]} />
        </div>
        {isContract && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
            {item.status === 'confirmed' ? <Badge color={B.green}>Confirmed</Badge> : <Badge color={B.amber}>Draft — not used yet</Badge>}
            {item.status === 'confirmed' && <span style={{ fontSize: 12, color: B.textLt }}>Saving an edit puts it back to draft until you confirm again.</span>}
          </div>
        )}

        <FileDropZone onFiles={upload} disabled={!!busy} label="Drop to upload to this record">
        <div style={{ fontWeight: 600, fontSize: 13, color: B.navy, margin: '4px 0 8px' }}>Files</div>
        {item.files.length ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
            {item.files.map(file => (
              <FileRow key={file.fileId} file={file} options={options} aiReady={aiReady}
                onDownload={() => download(file)} onRole={v => setFileRole(file, v)}
                onRescan={() => rescan(file)} onRemove={() => removeFile(file)} />
            ))}
          </div>
        ) : <div style={{ fontSize: 13, color: B.textLt, marginBottom: 10 }}>No files yet — drag files here or use Upload files.</div>}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ width: 210 }}>
            <Select label="Add as" value={uploadRole} onChange={setUploadRole}
              options={[...(isContract ? [{ value: 'auto', label: 'Detect from file name' }] : []),
                ...(options?.fileRoles || Object.keys(FILE_ROLE_LABELS)).map(r => ({ value: r, label: FILE_ROLE_LABELS[r] || r }))]} />
          </div>
          <label style={{ display: 'inline-block' }}>
            <input type="file" multiple style={{ display: 'none' }} disabled={!!busy}
              onChange={e => { upload(e.target.files); e.target.value = ''; }} />
            <span style={{ display: 'inline-block', padding: '8px 14px', borderRadius: 8, border: `1px solid ${B.border}`, fontSize: 13, cursor: busy ? 'default' : 'pointer', color: B.text }}>
              {busy === 'upload' ? 'Uploading…' : 'Upload files'}
            </span>
          </label>
          {isContract && (
            <Btn variant="ghost" onClick={extract} loading={busy === 'extract'} disabled={!!busy || !item.files.length || !aiReady}
              title={!aiReady ? 'No AI connection is set for Gov Bid Library' : undefined}>
              {item.suggestion ? 'Read files again' : 'Read files with AI'}
            </Btn>
          )}
        </div>
        </FileDropZone>
        {isContract && item.extractStatus === 'error' && item.extractError && (
          <div style={{ fontSize: 12, color: B.red, marginTop: 6 }}>Last read failed: {item.extractError}</div>
        )}

        {isContract && suggestion && (
          <Card style={{ marginTop: 14, background: B.surfaceMuted || B.bg }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: B.text }}>AI suggestions {item.extractedUTC ? `· ${fmt.date(item.extractedUTC)}` : ''}</div>
              {suggested.length > 0 && <Btn size="sm" variant="ghost" onClick={() => apply(suggested)}>Use all</Btn>}
            </div>
            {suggested.length ? suggested.map(k => (
              <div key={k} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, padding: '4px 0', borderTop: `1px solid ${B.border}` }}>
                <div style={{ width: 130, color: B.textMd, flexShrink: 0 }}>{FIELD_LABELS[k]}</div>
                <div style={{ flex: 1, color: B.text, whiteSpace: 'pre-wrap' }}>{showSuggestion(k, suggestion[k])}</div>
                <Btn size="sm" variant="ghost" onClick={() => apply([k])}>Use</Btn>
              </div>
            )) : <div style={{ fontSize: 13, color: B.textMd }}>Everything the AI found is already in the form.</div>}
            {suggestion.notRead?.length > 0 && (
              <div style={{ fontSize: 12, color: B.amber, marginTop: 6 }}>
                Not read ({suggestion.notRead.length}): {suggestion.notRead.map(n => (/lower priority/.test(n.reason) ? n.fileName : `${n.fileName} (${n.reason})`)).join('; ')}.
                {suggestion.notRead.some(n => /lower priority/.test(n.reason)) && ' Files are read award → contract → modification → evaluation → proposal → RFP; set a file’s type above to move it up.'}
              </div>
            )}
            <div style={{ fontSize: 12, color: B.textLt, marginTop: 6 }}>Check every figure against the files before you confirm — this is what proposals will cite.</div>
          </Card>
        )}

        <div style={{ fontWeight: 600, fontSize: 13, color: B.navy, margin: '16px 0 8px' }}>{isContract ? 'Facts' : 'Details'}</div>
        {isContract ? (
          <>
            <div className="harvus-responsive-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Input label="Title" value={f.title} onChange={v => set('title', v)} />
              <Input label="Agency / customer" value={f.agency} onChange={v => set('agency', v)} />
              <Input label="Contract number" value={f.contractRef} onChange={v => set('contractRef', v)} />
              <Select label="Outcome" value={f.outcome} onChange={v => set('outcome', v)}
                options={[{ value: '', label: 'Not set' }, ...Object.entries(OUTCOME_LABELS).map(([value, label]) => ({ value, label }))]} />
              <Select label="Our role" value={f.contractRole} onChange={v => set('contractRole', v)}
                options={[{ value: '', label: 'Not set' }, ...Object.entries(ROLE_LABELS).map(([value, label]) => ({ value, label }))]} />
              <Input label="Prime contractor (if we were a sub)" value={f.primeContractor} onChange={v => set('primeContractor', v)} />
              <Input label="Contract value ($)" value={f.contractAmount} onChange={v => set('contractAmount', v)} placeholder="2,450,000" />
              <Input label="NAICS" value={f.naicsCode} onChange={v => set('naicsCode', v)} />
              <Input label="Start (YYYY-MM-DD)" value={f.periodStartDate} onChange={v => set('periodStartDate', v)} />
              <Input label="End (YYYY-MM-DD)" value={f.periodEndDate} onChange={v => set('periodEndDate', v)} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
              <Input label="Reference (name, title, email, phone)" value={f.referenceContact} onChange={v => set('referenceContact', v)} />
              <Textarea label="Scope of work" rows={4} value={f.scope} onChange={v => set('scope', v)}
                placeholder="What was delivered, for whom, where, at what scale." />
              <Textarea label="How it went" rows={3} value={f.results} onChange={v => set('results', v)}
                placeholder="Ratings, metrics met, extensions — or why it was lost." />
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div className="harvus-responsive-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
              <Input label="Title" value={f.title} onChange={v => set('title', v)} placeholder="e.g. 2026 capability statement, Jane Doe — PM résumé" />
              <Select label="Type" value={f.materialType} onChange={v => set('materialType', v)}
                options={Object.entries(MATERIAL_LABELS).map(([value, label]) => ({ value, label }))} />
            </div>
            <Textarea label="Text (optional — paste reusable proposal text here)" rows={8} value={f.body} onChange={v => set('body', v)} />
          </div>
        )}
        {err && <div style={{ color: B.red, fontSize: 13, marginTop: 12 }}>{err}</div>}
      </div>
      <ModalFooter>
        <Btn variant="ghost" onClick={remove} disabled={!!busy} style={{ marginRight: 'auto', color: B.red }}>Delete</Btn>
        <Btn variant="ghost" onClick={close} disabled={!!busy}>Close</Btn>
        <Btn variant={isContract ? 'ghost' : undefined} onClick={() => save()} loading={busy === 'save'} disabled={!!busy || !dirty}>Save</Btn>
        {isContract && (
          <Btn onClick={() => save('confirmed')} loading={busy === 'confirm'} disabled={!!busy || (item.status === 'confirmed' && !dirty)}>
            {item.status === 'confirmed' ? 'Save & confirm' : 'Confirm'}
          </Btn>
        )}
      </ModalFooter>
    </Modal>
  );
}

// A draft contract whose AI suggestion is ready and still differs from it.
function hasSuggestions(i) {
  return i.kind === 'pastContract' && i.status !== 'confirmed' && i.extractStatus === 'ready' && !!i.suggestion;
}

const valueOf = (v) => (v === null || v === undefined ? '' : String(v));
const showValue = (k, v) => {
  if (v === null || v === undefined || v === '') return '';
  if (k === 'contractAmount') return Number.isFinite(Number(v)) ? fmt.currency(Number(v)) : String(v);
  if (k === 'contractRole') return ROLE_LABELS[v] || String(v);
  if (k === 'outcome') return OUTCOME_LABELS[v] || String(v);
  return String(v);
};

// Every suggested field that differs from the record, in form order.
export function suggestionDiffs(item) {
  const s = item.suggestion || {};
  const now = formFrom(item);
  return SUGGESTED_FIELDS
    .filter(k => s[k] !== undefined && s[k] !== null && valueOf(s[k]) !== '' && valueOf(s[k]) !== valueOf(now[k]))
    .map(k => ({ key: k, now: now[k], suggested: s[k], fills: valueOf(now[k]) === '' }));
}

// Steps through the draft contracts with suggestions, one at a time. A field
// the record leaves empty is ticked; a field that would replace something a
// person typed is not. Only ticked fields are saved.
function ReviewModal({ ids, items, nameOf, onChanged, onOpen, onClose }) {
  const { toast } = useToast();
  const [pos, setPos] = useState(0);
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(0);
  const byId = useMemo(() => new Map(items.map(i => [String(i.itemId).toLowerCase(), i])), [items]);
  const item = ids[pos] ? byId.get(String(ids[pos]).toLowerCase()) : null;
  const diffs = useMemo(() => (item ? suggestionDiffs(item) : []), [item]);

  useEffect(() => {
    setPicked(Object.fromEntries(diffs.map(d => [d.key, d.fills])));
    setErr(null);
  }, [item?.itemId]); // eslint-disable-line react-hooks/exhaustive-deps

  const next = () => { setErr(null); setPos(p => p + 1); };
  const save = async (status) => {
    const body = {};
    for (const d of diffs) if (picked[d.key]) body[d.key] = valueOf(d.suggested);
    if (status) body.status = status;
    if (!Object.keys(body).length) { next(); return; }
    setBusy(status || 'save'); setErr(null);
    try {
      const updated = await api.put(`govbid/library/${item.itemId}`, body);
      onChanged(updated);
      setDone(n => n + 1);
      toast.success(status === 'confirmed' ? 'Confirmed.' : 'Saved as draft.');
      next();
    } catch (e) { setErr(e.message); }
    finally { setBusy(null); }
  };

  if (!item) {
    return (
      <Modal title="Review AI suggestions" onClose={onClose} width={560}>
        <div style={{ padding: '8px 24px 16px', fontSize: 14, color: B.text }}>
          {pos >= ids.length ? `Done. ${done} of ${ids.length} contract${ids.length === 1 ? '' : 's'} saved.` : 'This record is no longer in the library.'}
        </div>
        <ModalFooter>
          {pos < ids.length && <Btn variant="ghost" onClick={next}>Next</Btn>}
          <Btn onClick={onClose}>Close</Btn>
        </ModalFooter>
      </Modal>
    );
  }

  const tickedCount = diffs.filter(d => picked[d.key]).length;
  const s = item.suggestion || {};
  return (
    <Modal title={`Review AI suggestions — ${pos + 1} of ${ids.length}`} onClose={onClose} width={900}>
      <div style={{ padding: '4px 24px 8px' }}>
        <div style={{ fontWeight: 600, fontSize: 15, color: B.text }}>{item.title || 'Untitled contract'}</div>
        <div style={{ fontSize: 12, color: B.textMd, marginTop: 2, marginBottom: 10 }}>
          <span style={{ color: B.navy, fontWeight: 600 }}>{nameOf(item.sageEntityId)}</span>
          {' · '}{item.files.length} file{item.files.length === 1 ? '' : 's'}
          {Array.isArray(s.read) && s.read.length > 0 && <> · read: {s.read.map(r => r.fileName || r).join(', ')}</>}
        </div>
        {!diffs.length ? (
          <div style={{ fontSize: 13, color: B.textMd, margin: '8px 0 12px' }}>The record already matches every suggestion.</div>
        ) : (
          <div style={{ border: `1px solid ${B.border}`, borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '32px 150px 1fr 1fr', gap: 10, padding: '8px 12px', fontSize: 12, fontWeight: 600, color: B.textMd, background: B.surfaceMuted || B.bg }}>
              <div><input type="checkbox" aria-label="Use all"
                checked={tickedCount === diffs.length}
                onChange={e => setPicked(Object.fromEntries(diffs.map(d => [d.key, e.target.checked])))} /></div>
              <div>Field</div><div>On the record</div><div>AI suggests</div>
            </div>
            {diffs.map(d => (
              <label key={d.key} style={{ display: 'grid', gridTemplateColumns: '32px 150px 1fr 1fr', gap: 10, padding: '8px 12px', fontSize: 13, borderTop: `1px solid ${B.border}`, cursor: 'pointer', alignItems: 'start' }}>
                <input type="checkbox" checked={!!picked[d.key]} onChange={e => setPicked(p => ({ ...p, [d.key]: e.target.checked }))} />
                <div style={{ color: B.textMd }}>{FIELD_LABELS[d.key]}</div>
                <div style={{ color: d.fills ? B.textLt : B.text, whiteSpace: 'pre-wrap' }}>{d.fills ? 'empty' : showValue(d.key, d.now)}</div>
                <div style={{ color: B.text, whiteSpace: 'pre-wrap' }}>
                  {showValue(d.key, d.suggested)}
                  {!d.fills && picked[d.key] && <div style={{ fontSize: 11, color: B.amber, marginTop: 2 }}>Replaces what is on the record</div>}
                </div>
              </label>
            ))}
          </div>
        )}
        {s.notRead?.length > 0 && (
          <div style={{ fontSize: 12, color: B.textMd, marginTop: 8 }}>Not read ({s.notRead.length}): {s.notRead.map(n => n.fileName).join('; ')}.</div>
        )}
        <div style={{ fontSize: 12, color: B.textLt, marginTop: 8 }}>
          Check every figure against the files before you confirm — this is what proposals will cite. To edit a value, open the record.
        </div>
        {err && <div style={{ color: B.red, fontSize: 13, marginTop: 8 }}>{err}</div>}
      </div>
      <ModalFooter>
        <Btn variant="ghost" onClick={() => setPos(p => Math.max(0, p - 1))} disabled={pos === 0 || !!busy}>Back</Btn>
        <Btn variant="ghost" onClick={next} disabled={!!busy}>Skip</Btn>
        <Btn variant="ghost" onClick={() => onOpen(item)} disabled={!!busy}>Open record</Btn>
        <Btn variant="ghost" onClick={() => save(null)} loading={busy === 'save'} disabled={!!busy || !tickedCount}>Save as draft</Btn>
        <Btn onClick={() => save('confirmed')} loading={busy === 'confirmed'} disabled={!!busy}>
          {tickedCount ? `Use ${tickedCount} & confirm` : 'Confirm'}
        </Btn>
      </ModalFooter>
    </Modal>
  );
}

// ── folder import ───────────────────────────────────────────────────────────

const busyScan = (f) => f.scanStatus === 'pending' || f.scanStatus === 'running';

// Where the AI is on one record: files still being read, the record read queued
// or running, or suggestions waiting for review.
function ReadState({ item }) {
  const reading = item.files.filter(busyScan).length;
  if (reading) return <span style={{ fontSize: 12, color: B.textMd, whiteSpace: 'nowrap' }}>Reading {item.files.length - reading}/{item.files.length} files…</span>;
  if (item.extractStatus === 'queued' || item.extractStatus === 'running') return <span style={{ fontSize: 12, color: B.textMd, whiteSpace: 'nowrap' }}>AI drafting facts…</span>;
  if (hasSuggestions(item)) return <Badge color={B.navy}>Suggestions ready</Badge>;
  if (item.extractStatus === 'error') return <span title={item.extractError || undefined} style={{ fontSize: 12, color: B.amber, whiteSpace: 'nowrap' }}>AI read failed</span>;
  return null;
}

// A line above the list while an import is being read, refreshed every 20 s.
function ImportProgress({ contracts, onRefresh, onReview }) {
  const files = contracts.flatMap(c => c.files);
  const reading = files.filter(busyScan).length;
  const drafting = contracts.filter(c => c.extractStatus === 'queued' || c.extractStatus === 'running').length;
  const review = contracts.filter(hasSuggestions).length;
  const active = reading > 0 || drafting > 0;
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(onRefresh, 20000);
    return () => clearInterval(id);
  }, [active, onRefresh]);
  if (!active && !review) return null;
  return (
    <Card style={{ marginBottom: 10, padding: '10px 14px', fontSize: 13, color: B.text, background: B.surfaceMuted || B.bg }}>
      {active ? (
        <>AI is working through the library in the background — {reading ? `${files.length - reading} of ${files.length} files read` : 'every file read'}
          {drafting ? `, ${drafting} contract${drafting === 1 ? '' : 's'} waiting to be drafted` : ''}. You can leave this page.</>
      ) : null}
      {review > 0 && (
        <div style={{ marginTop: active ? 4 : 0, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span>{review} draft contract{review === 1 ? ' has' : 's have'} AI suggestions ready — check the figures against the files, then confirm.</span>
          <Btn size="sm" onClick={onReview}>Review all</Btn>
        </div>
      )}
    </Card>
  );
}

const UPLOAD_CONCURRENCY = 4;
const mb = (n) => `${(n / 1048576).toFixed(n >= 1048576 * 10 ? 0 : 1)} MB`;

function ImportModal({ initialEntityId, items: allItems, companies, onDone, onClose }) {
  const { toast } = useToast();
  const [entityId, setEntityId] = useState(initialEntityId || '');
  const items = useMemo(() => allItems.filter(i => String(i.sageEntityId).toLowerCase() === String(entityId).toLowerCase()), [allItems, entityId]);
  const companyName = (companies.find(c => String(c.value).toLowerCase() === String(entityId).toLowerCase()) || {}).label;
  const [entries, setEntries] = useState(null);
  const [container, setContainer] = useState(undefined);
  const [excluded, setExcluded] = useState(() => new Set());
  const [phase, setPhase] = useState('pick'); // pick · review · uploading · done
  const [progress, setProgress] = useState({ done: 0, total: 0, failed: [] });
  const [err, setErr] = useState(null);

  const plan = useMemo(() => (entries ? planLibraryImport(entries, { container }) : null), [entries, container]);
  const chosen = plan ? plan.records.filter(r => !excluded.has(r.key)) : [];
  const contractsChosen = chosen.filter(r => r.kind === 'pastContract');
  const existingTitles = useMemo(() => new Set(items.map(i => `${i.kind}|${String(i.title || '').trim().toLowerCase()}`)), [items]);

  // An upload is the browser's work: leaving the page stops it.
  useEffect(() => {
    if (phase !== 'uploading') return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [phase]);

  const take = (list) => {
    if (!list.length) { setErr('No files found in that folder.'); return; }
    setEntries(list); setContainer(undefined); setExcluded(new Set()); setErr(null); setPhase('review');
  };

  const toggle = (key) => setExcluded(s => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });

  const start = async () => {
    setPhase('uploading'); setErr(null);
    try {
      const res = await api.post('govbid/library-import', {
        entityId, records: chosen.map(r => ({ key: r.key, kind: r.kind, title: r.title })),
      });
      const idByKey = new Map(res.records.map(x => [x.key, x.itemId]));
      const withIds = chosen.map(r => ({ ...r, itemId: idByKey.get(r.key) })).filter(r => r.itemId);
      const existingFiles = {};
      for (const it of items) existingFiles[it.itemId] = it.files;
      const queue = uploadQueue(withIds, existingFiles);
      const left = new Map();
      for (const q of queue) left.set(q.itemId, (left.get(q.itemId) || 0) + 1);
      const contractIds = new Set(withIds.filter(r => r.kind === 'pastContract').map(r => r.itemId));
      // A record with nothing new to upload is queued straight away.
      const finish = (ids) => {
        const list = ids.filter(id => contractIds.has(id));
        if (list.length) api.post('govbid/library-import/finish', { itemIds: list }).catch(() => { /* Read files with AI is the way back */ });
      };
      finish([...contractIds].filter(id => !left.has(id)));

      const failed = [];
      let done = 0;
      setProgress({ done: 0, total: queue.length, failed: [] });
      await runPool(queue, UPLOAD_CONCURRENCY, async (q) => {
        try {
          const { fileId, uploadUrl } = await api.post(`govbid/library/${q.itemId}/files`, {
            fileName: q.file.name, contentType: q.file.type || 'application/octet-stream', sizeBytes: q.file.size, folderPath: q.folderPath,
          });
          const put = await fetch(uploadUrl, {
            method: 'PUT', body: q.file,
            headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': q.file.type || 'application/octet-stream' },
          });
          if (!put.ok) throw new Error(`upload failed (${put.status})`);
          await api.post(`govbid/libfiles/${fileId}/confirm`, {});
        } catch (e) {
          failed.push({ name: q.folderPath ? `${q.folderPath}/${q.file.name}` : q.file.name, reason: e.message });
        } finally {
          done += 1;
          const n = left.get(q.itemId) - 1;
          left.set(q.itemId, n);
          if (n === 0) finish([q.itemId]);
          setProgress({ done, total: queue.length, failed: failed.slice() });
        }
      });
      setPhase('done');
      onDone();
      toast.success(`Imported ${queue.length - failed.length} file${queue.length - failed.length === 1 ? '' : 's'} into ${withIds.length} record${withIds.length === 1 ? '' : 's'}.`);
    } catch (e) { setErr(e.message); setPhase('review'); }
  };

  const busy = phase === 'uploading';
  return (
    <Modal title={`Import folders${companyName ? ` — ${companyName}` : ''}`} onClose={busy ? () => {} : onClose} width={820}>
      <div style={{ padding: '4px 24px 8px', fontSize: 13, color: B.text }}>
        {(phase === 'pick' || phase === 'review') && (
          <div style={{ width: 360, marginBottom: 12 }}>
            <Select label="Company these folders belong to" value={entityId} onChange={setEntityId}
              options={[{ value: '', label: 'Choose a company…' }, ...companies]} />
          </div>
        )}
        {phase === 'pick' && (
          <>
            <div style={{ lineHeight: 1.55, marginBottom: 12 }}>
              Pick the folder that holds this company's projects. <b>Each folder inside it becomes a draft past contract</b> (e.g. FDEM, Texas GLO),
              and every file inside — sub-folders like “NEW RFP” included — is attached to it. A folder named like
              “Capability statements” or “Résumés” becomes company material instead. Files loose at the top are left out and listed.
            </div>
            <div style={{ color: B.textMd, lineHeight: 1.55, marginBottom: 14 }}>
              AI then reads every file, and once a contract's files are read it drafts the contract's facts as suggestions.
              Nothing is confirmed until you review it.
            </div>
            <div
                onDragOver={e => e.preventDefault()}
                onDrop={async e => { e.preventDefault(); e.stopPropagation(); take(await collectEntriesFromDataTransfer(e.dataTransfer)); }}
              style={{ border: `2px dashed ${B.border}`, borderRadius: 10, padding: 28, textAlign: 'center' }}>
              <div style={{ marginBottom: 10, color: B.textMd }}>Drag the folder here, or</div>
              <label style={{ display: 'inline-block' }}>
                <input type="file" webkitdirectory="" directory="" multiple style={{ display: 'none' }}
                  onChange={e => { take(collectEntriesFromInputFiles(e.target.files)); e.target.value = ''; }} />
                <span style={{ display: 'inline-block', padding: '8px 14px', borderRadius: 8, background: B.navy, color: '#fff', cursor: 'pointer' }}>Choose folder</span>
              </label>
            </div>
          </>
        )}

        {phase === 'review' && plan && (
          <>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
              <div><b>{contractsChosen.length}</b> contract{contractsChosen.length === 1 ? '' : 's'} · <b>{chosen.length - contractsChosen.length}</b> material · <b>{chosen.reduce((n, r) => n + r.files.length, 0)}</b> files · {mb(chosen.reduce((n, r) => n + r.files.reduce((m, f) => m + f.file.size, 0), 0))}</div>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', color: B.textMd }}>
                <input type="checkbox" checked={plan.container} onChange={e => setContainer(e.target.checked)} />
                The folder I picked holds the projects
              </label>
            </div>
            {plan.container && looksLikeSubfolders(plan.records) && (
              <div style={{ color: B.amber, marginBottom: 8 }}>These look like the sub-folders of ONE project (RFP, award, agreement …). If you picked a single project's folder, untick the box above.</div>
            )}
            {!plan.container && plan.records.length === 1 && (
              <div style={{ color: B.amber, marginBottom: 8 }}>This reads the whole folder as ONE contract. If it holds several projects, tick the box above.</div>
            )}
            <div style={{ maxHeight: 360, overflowY: 'auto', border: `1px solid ${B.border}`, borderRadius: 8 }}>
              {plan.records.map(r => {
                const subs = [...new Set(r.files.map(f => f.folderPath.split('/')[0]).filter(Boolean))];
                const exists = existingTitles.has(`${r.kind}|${r.title.toLowerCase()}`);
                return (
                  <label key={r.key} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 12px', borderBottom: `1px solid ${B.border}`, cursor: 'pointer', opacity: excluded.has(r.key) ? 0.5 : 1 }}>
                    <input type="checkbox" checked={!excluded.has(r.key)} onChange={() => toggle(r.key)} style={{ marginTop: 3 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>
                        {r.title} <span style={{ fontWeight: 400, color: B.textLt }}>· {r.kind === 'material' ? 'company material' : 'past contract'}{exists ? ' · adds to the existing record' : ''}</span>
                      </div>
                      <div style={{ fontSize: 12, color: B.textMd }}>
                        {r.files.length} file{r.files.length === 1 ? '' : 's'}{subs.length ? ` · in ${subs.slice(0, 6).join(', ')}${subs.length > 6 ? ` +${subs.length - 6}` : ''}` : ''}
                      </div>
                    </div>
                  </label>
                );
              })}
              {!plan.records.length && <div style={{ padding: 12, color: B.textLt }}>No project folders found.</div>}
            </div>
            {plan.loose.length > 0 && (
              <div style={{ marginTop: 8, color: B.amber }}>
                Left out — not in any project folder ({plan.loose.length}): {plan.loose.slice(0, 8).map(l => l.name).join(', ')}{plan.loose.length > 8 ? '…' : ''}. Move them into a project folder, or add them to a record by hand.
              </div>
            )}
            {plan.skipped.length > 0 && (
              <div style={{ marginTop: 6, color: B.textMd }}>
                Skipped ({plan.skipped.length}): {plan.skipped.slice(0, 8).map(s => `${s.name} (${s.reason})`).join(', ')}{plan.skipped.length > 8 ? '…' : ''}
              </div>
            )}
            <div style={{ marginTop: 10, color: B.textLt, fontSize: 12 }}>
              Keep this page open while files upload. After that, AI reads them in the background and you can leave.
              A record whose name already exists gets the new files added, and a file already on it (same name and size) is not uploaded twice.
            </div>
          </>
        )}

        {(phase === 'uploading' || phase === 'done') && (
          <>
            <div style={{ marginBottom: 8 }}>
              {phase === 'uploading' ? 'Uploading — keep this page open…' : 'Upload finished. AI is reading the files in the background; contracts get drafted facts as their files are read.'}
            </div>
            <div style={{ height: 8, background: B.border, borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progress.total ? (progress.done / progress.total) * 100 : 100}%`, background: B.teal, transition: 'width .3s' }} />
            </div>
            <div style={{ marginTop: 6, color: B.textMd }}>{progress.done} of {progress.total} files{progress.failed.length ? ` · ${progress.failed.length} failed` : ''}</div>
            {progress.failed.length > 0 && (
              <div style={{ marginTop: 8, color: B.red, maxHeight: 140, overflowY: 'auto' }}>
                {progress.failed.map((f, i) => <div key={i}>{f.name} — {f.reason}</div>)}
                <div style={{ color: B.textMd, marginTop: 4 }}>Run the import again with the same folder to retry: files already uploaded are skipped.</div>
              </div>
            )}
          </>
        )}
        {err && <div style={{ color: B.red, marginTop: 10 }}>{err}</div>}
      </div>
      <ModalFooter>
        {phase === 'review' && <Btn variant="ghost" onClick={() => { setEntries(null); setPhase('pick'); }} style={{ marginRight: 'auto' }}>Pick another folder</Btn>}
        <Btn variant="ghost" onClick={onClose} disabled={busy}>{phase === 'done' ? 'Close' : 'Cancel'}</Btn>
        {phase === 'review' && <Btn onClick={start} disabled={!chosen.length || !entityId}>Import {chosen.length} record{chosen.length === 1 ? '' : 's'}</Btn>}
      </ModalFooter>
    </Modal>
  );
}
